import { useCallback, useMemo } from 'react';

import {
  SlashCommandDto,
  useBulkImportSlashCommandsMutation,
  useCreateSlashCommandMutation,
  useDeleteSlashCommandMutation,
  useListSlashCommandsQuery,
  useUpdateSlashCommandMutation,
} from '../../../services/api/slashCommandsService';
import {
  validateCommandName,
  validateDescription,
  validatePromptText,
} from './slashCommandUtils';
import { SlashCommand, SlashCommandSharePayload } from './types';

// ---------------------------------------------------------------------------
// Helper – map backend DTO → frontend SlashCommand shape
// ---------------------------------------------------------------------------

const toSlashCommand = (dto: SlashCommandDto): SlashCommand => ({
  id: dto.id,
  name: dto.name,
  description: dto.description,
  promptText: dto.prompt_text,
  createdAt: dto.created_at,
  updatedAt: dto.modified_at,
});

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useSlashCommands = () => {
  const { data, isLoading } = useListSlashCommandsQuery();
  const [createMutation] = useCreateSlashCommandMutation();
  const [updateMutation] = useUpdateSlashCommandMutation();
  const [deleteMutation] = useDeleteSlashCommandMutation();
  const [bulkImportMutation] = useBulkImportSlashCommandsMutation();

  const commands: SlashCommand[] = useMemo(
    () => (data?.commands ?? []).map(toSlashCommand),
    [data],
  );

  // ---------- add ----------
  const addCommand = useCallback(
    async (input: {
      name: string;
      description: string;
      promptText: string;
    }): Promise<SlashCommand | string> => {
      const nameError = validateCommandName(input.name);
      if (nameError) return nameError;

      const descError = validateDescription(input.description);
      if (descError) return descError;

      const promptError = validatePromptText(input.promptText);
      if (promptError) return promptError;

      try {
        const dto = await createMutation({
          name: input.name.toLowerCase(),
          description: input.description,
          prompt_text: input.promptText,
        }).unwrap();
        return toSlashCommand(dto);
      } catch (err) {
        const apiErr = err as { data?: { error?: string } };
        return apiErr?.data?.error ?? 'Failed to create command';
      }
    },
    [createMutation],
  );

  // ---------- update ----------
  const updateCommand = useCallback(
    async (
      id: string,
      input: Partial<Pick<SlashCommand, 'name' | 'description' | 'promptText'>>,
    ): Promise<string | null> => {
      if (input.name) {
        const nameError = validateCommandName(input.name);
        if (nameError) return nameError;
      }

      if (input.description !== undefined) {
        const descError = validateDescription(input.description);
        if (descError) return descError;
      }

      if (input.promptText !== undefined) {
        const promptError = validatePromptText(input.promptText);
        if (promptError) return promptError;
      }

      const body: Record<string, string> = {};
      if (input.name !== undefined) body.name = input.name.toLowerCase();
      if (input.description !== undefined) body.description = input.description;
      if (input.promptText !== undefined) body.prompt_text = input.promptText;

      try {
        await updateMutation({ id, data: body }).unwrap();
        return null;
      } catch (err) {
        const apiErr = err as { data?: { error?: string } };
        return apiErr?.data?.error ?? 'Failed to update command';
      }
    },
    [updateMutation],
  );

  // ---------- delete ----------
  const deleteCommand = useCallback(
    async (id: string): Promise<void> => {
      await deleteMutation(id).unwrap();
    },
    [deleteMutation],
  );

  // ---------- import ----------
  const importCommands = useCallback(
    async (
      payloadCommands: SlashCommandSharePayload['commands'],
    ): Promise<number> => {
      const result = await bulkImportMutation({
        commands: payloadCommands.map((cmd) => ({
          name: cmd.name.toLowerCase(),
          description: cmd.description || '',
          prompt_text: cmd.promptText,
        })),
      }).unwrap();
      return result.imported_count;
    },
    [bulkImportMutation],
  );

  // ---------- lookup ----------
  const getCommandByName = useCallback(
    (name: string): SlashCommand | undefined => {
      return commands.find(
        (c) => c.name.toLowerCase() === name.toLowerCase(),
      );
    },
    [commands],
  );

  return {
    commands,
    isLoading,
    addCommand,
    updateCommand,
    deleteCommand,
    importCommands,
    getCommandByName,
  };
};
